/**
 * Shared impact-path graph helpers — ancestry over the PRIMARY-parent tree, used
 * to decide which borrowed (multi-parent) edges are meaningful.
 *
 * A borrowed edge parent→child is:
 *   - REDUNDANT if `parent` is already an ancestor of `child` (already linked
 *     through the tree → a direct connector adds no information), or
 *   - a BACK-EDGE / cycle if `parent` is a descendant of `child`.
 * Only genuine cross-branch links carry new information. This is the single
 * source of truth for BOTH rendering (which dashed edges to draw) and creation
 * (which parents the add-parent / AI-offer flows are allowed to propose).
 */

/** Map nodeId → its primary parent id (the tree skeleton). */
export function buildPrimaryParentMap(pathways = []) {
  const m = {};
  pathways.forEach((p) => { m[p.id] = p.parentId || p.parent_id || null; });
  return m;
}

/** Walk up the primary tree from `ofNode` — is `maybeAncestor` an ancestor? */
export function isAncestor(primaryParentOf, maybeAncestor, ofNode) {
  let cur = primaryParentOf[ofNode];
  let guard = 0;
  while (cur && guard++ < 500) {
    if (cur === maybeAncestor) return true;
    cur = primaryParentOf[cur];
  }
  return false;
}

/**
 * Is a borrowed edge parent→child meaningful (genuine cross-branch)?
 * False when redundant (parent already an ancestor) or cyclic (parent a
 * descendant). Use this to filter both rendered edges and pickable parents.
 */
export function isMeaningfulConnection(primaryParentOf, parentId, childId) {
  if (!parentId || !childId || parentId === childId) return false;
  if (isAncestor(primaryParentOf, parentId, childId)) return false; // redundant
  if (isAncestor(primaryParentOf, childId, parentId)) return false; // back-edge / cycle
  return true;
}

/**
 * Merge multi-parent connections into each child's `parentIds` (PRIMARY first),
 * dropping redundant/cyclic edges. Layout still uses parentIds[0] only, so
 * borrowed parents add dashed overlay edges without moving anything.
 */
export function mergeConnectionsIntoPathways(pathways = [], connections = []) {
  if (!connections || connections.length === 0) return pathways;
  const ppo = buildPrimaryParentMap(pathways);
  const extra = {};
  connections.forEach((c) => {
    const child = c.childId || c.child_node_id;
    const parent = c.parentId || c.parent_node_id;
    if (isMeaningfulConnection(ppo, parent, child)) {
      (extra[child] = extra[child] || []).push(parent);
    }
  });
  return pathways.map((p) => {
    const sec = extra[p.id];
    if (!sec || !sec.length) return p;
    const primary = p.parentId || p.parent_id;
    return { ...p, parentIds: [primary, ...sec].filter(Boolean) };
  });
}

/**
 * Candidate parents for a given child in the manual "add parent" flow: every
 * node that would form a meaningful (non-redundant, non-cyclic) borrowed edge,
 * excluding the child's existing parents. Optionally restrict to same scenario.
 */
export function candidateParentsFor(pathways = [], childId, { sameScenarioOnly = true } = {}) {
  const ppo = buildPrimaryParentMap(pathways);
  const child = pathways.find((p) => p.id === childId);
  if (!child) return [];
  const childScenario = child.scenarioId || child.scenario_id;
  const existingParent = ppo[childId];
  return pathways.filter((p) => {
    if (p.id === childId || p.id === existingParent) return false;
    if (p.is_scenario_starter) return false; // variables/roots aren't borrowed parents
    if (sameScenarioOnly && (p.scenarioId || p.scenario_id) !== childScenario) return false;
    return isMeaningfulConnection(ppo, p.id, childId);
  });
}
